import { type ApiUnbilledContainerRentGroup } from '@Model';

/** Whether the group has lines and every one of them is selected. */
// @ai-generated 2026-10-21T14:02:47+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function isRentGroupSelected(group: ApiUnbilledContainerRentGroup, selected: ReadonlySet<string>): boolean {
    return group.lines.length > 0 && group.lines.every((line) => selected.has(line.id));
}

/**
 * The selection once exactly the groups with the given ids are ticked: every line of a newly ticked group is
 * selected, and every line of a group that was fully selected and is no longer ticked is deselected.
 */
// @ai-generated 2026-10-21T14:02:47+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function withTickedRentGroups(
    selected: ReadonlySet<string>,
    groups: readonly ApiUnbilledContainerRentGroup[],
    tickedIds: readonly string[],
): ReadonlySet<string> {
    const ticked = new Set(tickedIds);
    const next = new Set(selected);

    for (const group of groups) {
        if (ticked.has(group.id)) {
            group.lines.forEach((line) => next.add(line.id));
        } else if (isRentGroupSelected(group, selected)) {
            group.lines.forEach((line) => next.delete(line.id));
        }
    }

    return next;
}

/** The selection once exactly the given lines of the group are ticked, with the rest of the selection as it was. */
// @ai-generated 2026-10-21T14:02:47+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function withTickedRentLines(
    selected: ReadonlySet<string>,
    group: ApiUnbilledContainerRentGroup,
    tickedIds: readonly string[],
): ReadonlySet<string> {
    const ticked = new Set(tickedIds);
    const next = new Set(selected);

    for (const line of group.lines) {
        if (ticked.has(line.id)) {
            next.add(line.id);
        } else {
            next.delete(line.id);
        }
    }

    return next;
}

/** The selection without the standing times that are not listed any more. */
// @ai-generated 2026-10-21T14:02:47+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function stillListedRent(
    selected: ReadonlySet<string>,
    groups: readonly ApiUnbilledContainerRentGroup[],
): ReadonlySet<string> {
    const listed = new Set(groups.flatMap((group) => group.lines.map((line) => line.id)));
    const kept = [...selected].filter((id) => listed.has(id));

    return kept.length === selected.size ? selected : new Set(kept);
}
